
import { encryptDataSync, decryptDataSync, encryptData } from '@/utils/encryption';
import { FeatureModelConfigMap } from './types';
import { defaultConfig, STORAGE_KEY } from './defaults';
import { toast } from 'sonner';

// Helper function to safely parse the configuration data
export const parseConfigFromStorage = (): FeatureModelConfigMap => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (!storedData) {
      console.log("No stored configuration found, using defaults");
      return { ...defaultConfig };
    }
    
    // Try to decrypt and parse with both methods
    try {
      // First try with the sync method
      const decrypted = decryptDataSync(storedData);
      const parsedConfig = JSON.parse(decrypted);
      console.log("Successfully loaded config with sync decryption method");
      
      // Debug log to check what we loaded
      console.log("Loaded features with API keys:", 
        Object.keys(parsedConfig).filter(key => parsedConfig[key]?.apiKey && parsedConfig[key].apiKey.length > 0));
      
      // Validate the parsed configuration
      return validateAndMergeConfig(parsedConfig);
    } catch (syncError) {
      console.error('Error parsing with sync method, trying async method', syncError);
      
      // If sync failed, clear localStorage to avoid future issues
      localStorage.removeItem(STORAGE_KEY);
      
      // Return default config since we can't wait for async here
      return { ...defaultConfig };
    }
  } catch (error) {
    console.error('Error loading feature configs', error);
    toast.error('Error loading saved configurations');
    // Clear potentially corrupted data
    localStorage.removeItem(STORAGE_KEY);
    return { ...defaultConfig };
  }
};

// Helper function to validate and merge configuration with defaults
export const validateAndMergeConfig = (parsedConfig: any): FeatureModelConfigMap => {
  const validConfig: FeatureModelConfigMap = { ...defaultConfig };
  
  // Check if the parsed config is actually an object
  if (!parsedConfig || typeof parsedConfig !== 'object') {
    console.error('Invalid configuration format, using defaults');
    return validConfig;
  }
  
  Object.keys(defaultConfig).forEach((featureId) => {
    if (parsedConfig[featureId]) {
      validConfig[featureId] = {
        ...defaultConfig[featureId],
        ...parsedConfig[featureId]
      };
      
      // Ensure apiKey is included when available
      if (parsedConfig[featureId].apiKey) {
        validConfig[featureId].apiKey = parsedConfig[featureId].apiKey;
        console.log(`API key found for ${featureId}, length: ${parsedConfig[featureId].apiKey.length}`);
      }
      
      // Check for usage reset
      const lastReset = new Date(validConfig[featureId].lastReset);
      const today = new Date();
      if (lastReset.getDate() !== today.getDate() || 
          lastReset.getMonth() !== today.getMonth() || 
          lastReset.getFullYear() !== today.getFullYear()) {
        validConfig[featureId].usageCount = 0;
        validConfig[featureId].lastReset = today.toISOString();
      }
    }
  });
  
  return validConfig;
};

// Save configuration to storage
export const saveConfigToStorage = async (configs: FeatureModelConfigMap): Promise<void> => {
  try {
    const dataToSave = JSON.stringify(configs);
    
    // First try using the sync method to ensure immediate save
    try {
      const encryptedSync = encryptDataSync(dataToSave);
      localStorage.setItem(STORAGE_KEY, encryptedSync);
      console.log("Configuration saved successfully with sync method");
      
      // Debug log to confirm we're saving API keys
      const featuresWithKeys = Object.keys(configs).filter(
        key => configs[key as keyof typeof configs]?.apiKey && 
              configs[key as keyof typeof configs].apiKey.length > 0
      );
      console.log("Saved features with API keys:", featuresWithKeys);
    } catch (syncError) {
      console.error('Error saving with sync method, trying async method', syncError);
      
      // Try async method as backup
      const encrypted = await encryptData(dataToSave);
      localStorage.setItem(STORAGE_KEY, encrypted);
      console.log("Configuration saved with async method");
    }
  } catch (error) {
    console.error('All encryption methods failed', error);
    toast.error('Failed to save configuration securely');
  }
};

// Verify stored configuration integrity
export const verifyStoredConfig = (): void => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (storedData) {
      console.log(`Storage key exists with ${storedData.length} characters`);
      
      // Check if we can decrypt what we stored
      try {
        const decrypted = decryptDataSync(storedData);
        const hasApiKeys = JSON.parse(decrypted);
        
        // Check for API keys
        const keysExist = Object.keys(hasApiKeys).some(
          feature => hasApiKeys[feature]?.apiKey && hasApiKeys[feature].apiKey.length > 0
        );
        
        console.log(`Decryption successful, API keys exist: ${keysExist}`);
        
        // Log which features have API keys
        if (keysExist) {
          const featuresWithKeys = Object.keys(hasApiKeys).filter(
            key => hasApiKeys[key]?.apiKey && hasApiKeys[key].apiKey.length > 0
          );
          console.log("Features with API keys:", featuresWithKeys);
        }
      } catch (error) {
        console.error('Unable to verify stored data integrity', error);
      }
    } else {
      console.log('No stored configuration found in localStorage');
    }
  } catch (error) {
    console.error('Error checking localStorage', error);
  }
};
